import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

// Mapping de rutas a labels legibles
const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  projects: 'Proyectos',
  team: 'Equipo',
  settings: 'Configuración',
  'virtual-agent': 'Asistente Virtual',
  'ai-predictive': 'IA Predictiva',
  analytics: 'Análisis',
  resources: 'Recursos',
  automation: 'Automatización',
  innovation: 'Innovación',
  genetics: 'Genética de Proyectos',
  training: 'Capacitación',
  'user-manual': 'Manual de Usuario',
  'project-members': 'Miembros',
  'project-planner': 'Planificador',
  'bulk-import': 'Importar Proyectos',
  communication: 'Comunicación',
  reports: 'Reportes',
  admin: 'Administración',
  users: 'Usuarios',
  logs: 'Logs',
};

export const AutoBreadcrumbs: React.FC = () => {
  const location = useLocation();

  const breadcrumbItems = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    if (pathSegments.length === 0) return [];

    const items: BreadcrumbItem[] = [];
    let currentPath = '';

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Skip UUIDs in breadcrumbs (show them as the actual item name later)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
      
      if (isUUID) {
        items.push({
          label: 'Detalle',
          href: index < pathSegments.length - 1 ? currentPath : undefined,
        });
      } else {
        const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        items.push({
          label,
          href: index < pathSegments.length - 1 ? currentPath : undefined,
        });
      }
    });

    return items;
  }, [location.pathname]);

  // Don't render breadcrumbs on home page or if there's only the dashboard
  if (breadcrumbItems.length === 0) return null;

  return (
    <nav 
      aria-label="Breadcrumb" 
      className="py-3 px-1 animate-fade-in"
    >
      <ol
        itemScope
        itemType="https://schema.org/BreadcrumbList"
        className="flex items-center space-x-1 text-sm"
      >
        {/* Home */}
        <li
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
          className="flex items-center"
        >
          <Link
            to="/dashboard"
            itemProp="item"
            className="flex items-center p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200"
          >
            <Home className="w-4 h-4" />
            <span itemProp="name" className="sr-only">Inicio</span>
          </Link>
          <meta itemProp="position" content="1" />
        </li>

        {breadcrumbItems.map((item, index) => (
          <li
            key={index}
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
            className="flex items-center"
          >
            <ChevronRight className="w-4 h-4 text-muted-foreground/50 mx-1" />
            {item.href ? (
              <Link
                to={item.href}
                itemProp="item"
                className="px-2 py-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200"
              >
                <span itemProp="name">{item.label}</span>
              </Link>
            ) : (
              <span 
                itemProp="name" 
                className="px-2 py-1 rounded-lg text-foreground font-medium bg-primary/5"
              >
                {item.label}
              </span>
            )}
            <meta itemProp="position" content={String(index + 2)} />
          </li>
        ))}
      </ol>
    </nav>
  );
};
